import React, { useState } from 'react';
import PropTypes from 'prop-types';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableHead from '@material-ui/core/TableHead';
import TablePagination from '@material-ui/core/TablePagination';
import TableRow from '@material-ui/core/TableRow';

import Label from 'core/components/label/label';
import CollapsibleSection from 'core/components/collapsible-section';
import useStyles from './aor-run-detail.styles';

const ROWS_PER_PAGE = 15;

/**
 * One outcome table (success or failed) from the apply's stored output. Rows are the worker's
 * `details`; failed rows also carry the full error.
 *
 * Deliberately knows nothing about any entity: the `variant` supplies the columns and the row
 * component, so adding members or staff users needs no change here. See `result-variants.js`.
 */
const ApplyResultsTable = ({ title, rows, showError, variant }) => {
  const classes = useStyles();
  const [page, setPage] = useState(0);

  if (!rows.length) {
    return null;
  }

  const { columns, rowKey, Row } = variant;
  const pageRows = rows.slice(page * ROWS_PER_PAGE, (page + 1) * ROWS_PER_PAGE);

  return (
    <CollapsibleSection
      className={classes.resultsSection}
      content={<Label type="infoTitleSmall">{`${title} (${rows.length})`}</Label>}
      initialOpen
    >
      <div className={classes.tableScroll}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell key={column}>{column}</TableCell>
              ))}
              {showError && <TableCell>Error</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {pageRows.map((row, index) => {
              const details = row.details ?? row;
              return (
                <Row
                  // The record id is the natural key, but a row whose worker failed before reading
                  // the record has none — `||`, not `??`, because that case is an empty string, and
                  // several of them would otherwise collide on one key.
                  key={rowKey(details) || `row-${page}-${index}`}
                  details={details}
                  error={row.error}
                  showError={showError}
                />
              );
            })}
          </TableBody>
        </Table>
      </div>
      {rows.length > ROWS_PER_PAGE && (
        <TablePagination
          component="div"
          count={rows.length}
          page={page}
          onPageChange={(_event, newPage) => setPage(newPage)}
          rowsPerPage={ROWS_PER_PAGE}
          rowsPerPageOptions={[ROWS_PER_PAGE]}
        />
      )}
    </CollapsibleSection>
  );
};

ApplyResultsTable.propTypes = {
  title: PropTypes.string.isRequired,
  rows: PropTypes.arrayOf(PropTypes.object),
  showError: PropTypes.bool,
  variant: PropTypes.shape({
    columns: PropTypes.arrayOf(PropTypes.string).isRequired,
    rowKey: PropTypes.func.isRequired,
    Row: PropTypes.elementType.isRequired
  }).isRequired
};

ApplyResultsTable.defaultProps = {
  rows: [],
  showError: false
};

export default ApplyResultsTable;
